import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, Building2, Square, ArrowLeft, MessageCircle, Map, Home, Calendar, Tag, Check, ImageIcon, ChevronLeft, ChevronRight, Eye, X } from 'lucide-react';
import { ShareButton } from '../components/common/ShareButton';
import { motion } from 'framer-motion';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { MapModal } from '../components/common/MapModal';
import { PropertyStatusBadge } from '../components/common/PropertyStatusBadge';
import { useCMS } from '../context/CMSContext';
import { categoryLabels, districtLabels, formatPrice, getWhatsAppUrl, getShareUrl } from '../utils/helpers';

const API_BASE = import.meta.env.VITE_CMS_API_URL || 'https://marveland-cms.jimsbond007.workers.dev';

const PropertyDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { getProperty, getAgent, cmsData, loading } = useCMS();
  const property = getProperty(id || '');
  const agent = property ? getAgent(property.agentId) : undefined;

  const [activeImage, setActiveImage] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [showMap, setShowMap] = useState(false);
  const [showFloorPlan, setShowFloorPlan] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxTouchStart, setLightboxTouchStart] = useState<number | null>(null);
  const [views, setViews] = useState<number | null>(null);

  // 瀏覽計數：回報一次＋顯示人氣徽章（第一方 Analytics，KV analytics:property:{id}）
  useEffect(() => {
    if (!id) return;
    fetch(`${API_BASE}/api/analytics/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ propertyId: id }),
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d && d.success) setViews((v) => (v === null ? 1 : v + 1)); })
      .catch(() => {});
    fetch(`${API_BASE}/api/analytics/property/${id}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d && typeof d.views === 'number') setViews(d.views); })
      .catch(() => {});
  }, [id]);

  // 等待 CMS 資料刷新完成後再判斷物業是否存在，避免靜態備份未包含新建物業時誤轉址
  if (loading && !property) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <div className="text-center">
          <div className="mb-4 h-8 w-8 animate-spin rounded-full border-4 border-gold-200 border-t-gold-600 mx-auto" />
          <p className="text-sm text-slate-500">載入物業資料中...</p>
        </div>
      </div>
    );
  }

  if (!property) {
    // 死連結落地頁（2026-08-28）：FB 舊貼文短連結指向已下載/不存在樓盤時，
    // 不再靜默彈去搜尋頁——明確告知＋推薦現有樓盤＋查詢 CTA
    const recommendations = cmsData.properties
      .filter((p) => p.id !== id)
      .sort((a, b) => Number(b.featured) - Number(a.featured))
      .slice(0, 6);
    const waLink = cmsData.siteConfig.social.whatsapp1 || cmsData.siteConfig.social.whatsapp2 || '';
    return (
      <div className="min-h-screen bg-white font-sans text-slate-900">
        <Header />
        <main className="container mx-auto px-4 pb-16 pt-28 md:pt-36">
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 md:p-8">
            <h1 className="text-xl font-bold text-slate-900 md:text-2xl">此樓盤已下架或不存在</h1>
            <p className="mt-2 text-sm text-slate-600">
              你瀏覽的樓盤可能已租售或被移除。以下為現有精選樓盤，或直接 WhatsApp 聯絡我們查詢類似單位。
            </p>
            {waLink && (
              <a
                href={waLink}
                target="_blank"
                rel="noreferrer"
                className="mt-4 inline-flex items-center gap-2 rounded-full bg-green-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-green-700"
              >
                <MessageCircle className="h-4 w-4" />
                WhatsApp 查詢類似樓盤
              </a>
            )}
          </div>

          {recommendations.length > 0 && (
            <div className="mt-8">
              <h2 className="mb-4 text-lg font-bold text-slate-900">精選現有樓盤</h2>
              <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                {recommendations.map((p) => (
                  <Link
                    key={p.id}
                    to={`/properties/${p.id}`}
                    className="group overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm transition hover:shadow-md"
                  >
                    <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                      {p.images[0] && (
                        <img src={p.images[0]} alt={p.title} className="h-full w-full object-cover transition group-hover:scale-105" loading="lazy" />
                      )}
                    </div>
                    <div className="p-3">
                      <p className="truncate text-sm font-semibold text-slate-900">{p.title}</p>
                      <p className="mt-1 text-sm font-bold text-gold-600">
                        {p.listingType === 'rent' ? `租 $${(p.rentPrice || p.price).toLocaleString()}` : `售 $${p.price.toLocaleString()}`}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
              <Link to="/properties" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-blue-800 hover:underline">
                <ArrowLeft className="h-4 w-4" />
                瀏覽全部樓盤
              </Link>
            </div>
          )}
        </main>
        <Footer />
      </div>
    );
  }

  const displayPrice =
    property.listingType === 'rent' && property.rentPrice
      ? formatPrice(property.rentPrice, 'HKD / 月')
      : formatPrice(property.price, 'HKD');

  const whatsappMessage = `你好 ${agent?.name || ''}，我想查詢「${property.title}」（樓盤編號：${property.code}），請與我聯絡，謝謝。`;
  const whatsappUrl = agent ? getWhatsAppUrl(agent.whatsapp, whatsappMessage) : '#';
  const shareUrl = getShareUrl(property.id);

  const images = property.images.length > 0 ? property.images : [property.images[0] || cmsData.hero.backgroundImage];

  // 燈箱：Esc 關閉、左右鍵導航；開啟時鎖定背景捲動
  useEffect(() => {
    if (!lightboxOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightboxOpen(false);
      if (e.key === 'ArrowLeft') setActiveImage((prev) => (prev - 1 + images.length) % images.length);
      if (e.key === 'ArrowRight') setActiveImage((prev) => (prev + 1) % images.length);
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightboxOpen, images.length]);
  const hasFloorPlan = Boolean(property.floorPlan);

  return (
    <div className="min-h-screen bg-white font-sans text-slate-900">
      <Header />

      <main className="pt-24 md:pt-[104px]">
        {/* Top bar */}
        <div className="border-b border-slate-100 bg-white">
          <div className="container mx-auto px-4 py-4">
            <Link
              to="/properties"
              className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-blue-800"
            >
              <ArrowLeft className="h-4 w-4" />
              搜尋其他樓盤
            </Link>
          </div>
        </div>

        {/* Hero gallery */}
        <section className="bg-[#f5f3ee] py-8 md:py-12">
          <div className="container mx-auto px-4">
            <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="mb-2 flex flex-wrap items-center gap-2 text-sm font-semibold text-slate-500">
                  <span className="rounded bg-slate-900 px-2 py-1 text-white">{property.code}</span>
                  <span>{property.location}</span>
                  <span className="h-1 w-1 rounded-full bg-slate-300" />
                  <span>{districtLabels[property.district] || property.location}</span>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-2xl font-bold text-slate-900 md:text-4xl">{property.title}</h1>
                  {views !== null && views > 0 && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-500">
                      <Eye className="h-3.5 w-3.5" /> {views} 人查看
                    </span>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {property.status !== 'available' ? (
                  <PropertyStatusBadge status={property.status} className="px-3 py-1.5 text-sm" pulse={false} />
                ) : (
                  <span className="bg-blue-800 px-3 py-1.5 text-sm font-bold text-white">
                    {property.listingType === 'sale' ? '放售' : property.listingType === 'rent' ? '放租' : '租售均可'}
                  </span>
                )}
                <span className="bg-gold-600 px-3 py-1.5 text-sm font-bold text-white">
                  {categoryLabels[property.category]}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_360px]">
              {/* Main image */}
              <div className="space-y-4">
                <div
                  className="group relative aspect-[16/9] w-full max-w-full max-h-[50vw] cursor-zoom-in overflow-hidden rounded-xl bg-slate-200 md:aspect-[16/10] md:max-h-[65vh]"
                  onClick={() => setLightboxOpen(true)}
                  onTouchStart={(e) => setTouchStart(e.targetTouches[0].clientX)}
                  onTouchMove={(e) => {
                    if (touchStart === null) return;
                    const diff = touchStart - e.targetTouches[0].clientX;
                    if (Math.abs(diff) > 50) {
                      e.preventDefault();
                    }
                  }}
                  onTouchEnd={(e) => {
                    if (touchStart === null) return;
                    const diff = touchStart - e.changedTouches[0].clientX;
                    if (diff > 50) {
                      setActiveImage((prev) => (prev + 1) % images.length);
                    } else if (diff < -50) {
                      setActiveImage((prev) => (prev - 1 + images.length) % images.length);
                    }
                    setTouchStart(null);
                  }}
                >
                  <div
                    className="flex h-full w-full transition-transform duration-500 ease-out"
                    style={{ transform: `translateX(-${activeImage * 100}%)` }}
                  >
                    {images.map((img, idx) => (
                      <div
                        key={idx}
                        className="relative h-full w-full shrink-0"
                      >
                        <img
                          src={img}
                          alt={`${property.title} ${idx + 1}`}
                          className="h-full w-full max-w-full object-cover"
                          draggable={false}
                        />
                      </div>
                    ))}
                  </div>

                  {/* 左上角張數計數器 */}
                  <div className="absolute left-3 top-3 z-10">
                    <span className="bg-slate-900/90 px-2.5 py-1 text-xs font-bold tracking-wide text-white backdrop-blur-sm">
                      {activeImage + 1}/{images.length}
                    </span>
                  </div>

                  {/* 左右切換箭頭 */}
                  {images.length > 1 && (
                    <>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveImage((prev) => (prev - 1 + images.length) % images.length);
                        }}
                        className="absolute left-2 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/90 p-2 text-slate-700 opacity-70 shadow-sm transition-opacity hover:bg-white group-hover:opacity-100"
                        aria-label="上一張"
                      >
                        <ChevronLeft className="h-5 w-5" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveImage((prev) => (prev + 1) % images.length);
                        }}
                        className="absolute right-2 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/90 p-2 text-slate-700 opacity-70 shadow-sm transition-opacity hover:bg-white group-hover:opacity-100"
                        aria-label="下一張"
                      >
                        <ChevronRight className="h-5 w-5" />
                      </button>
                    </>
                  )}

                  {/* 放大提示 */}
                  <div className="pointer-events-none absolute bottom-3 right-3 z-10 rounded-full bg-black/50 p-2 text-white opacity-0 backdrop-blur-sm transition-opacity group-hover:opacity-100">
                    <ImageIcon className="h-4 w-4" />
                  </div>
                </div>

                {images.length > 1 && (
                  <div className="flex gap-3 overflow-x-auto pb-2">
                    {images.map((img, idx) => (
                      <button
                        key={idx}
                        onClick={() => setActiveImage(idx)}
                        className={`relative h-20 w-28 flex-shrink-0 overflow-hidden rounded-lg border-2 ${
                          idx === activeImage ? 'border-blue-800' : 'border-transparent'
                        }`}
                      >
                        <img src={img} alt={`${property.title} ${idx + 1}`} className="h-full w-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Quick info card */}
              <div className="h-fit space-y-6 rounded-xl bg-white p-6 shadow-[0_16px_45px_-32px_rgba(15,23,42,0.6)]">
                <div>
                  <p className="mb-1 text-sm text-slate-500">
                    {property.listingType === 'rent' ? '每月租金' : property.listingType === 'sale_or_rent' ? '售價 / 租金' : '意向價'}
                  </p>
                  {property.listingType === 'sale_or_rent' ? (
                    <div className="space-y-1">
                      <p className="text-2xl font-bold text-slate-900">售 {formatPrice(property.price, 'HKD')}</p>
                      <p className="text-2xl font-bold text-slate-900">租 {formatPrice(property.rentPrice || 0, 'HKD / 月')}</p>
                    </div>
                  ) : (
                    <p className="text-3xl font-bold text-slate-900">{displayPrice}</p>
                  )}
                </div>

                <div className="space-y-3 border-t border-slate-100 pt-5 text-sm">
                  <div className="flex items-center gap-3">
                    <Square className="h-5 w-5 text-gold-700" />
                    <span className="text-slate-600">建築面積</span>
                    <span className="ml-auto font-bold">{property.areaGross.toLocaleString()} 呎</span>
                  </div>
                  {property.areaSaleable && (
                    <div className="flex items-center gap-3">
                      <Square className="h-5 w-5 text-gold-700" />
                      <span className="text-slate-600">實用面積</span>
                      <span className="ml-auto font-bold">{property.areaSaleable.toLocaleString()} 呎</span>
                    </div>
                  )}
                  <div className="flex items-center gap-3">
                    <Building2 className="h-5 w-5 text-gold-700" />
                    <span className="text-slate-600">物業類型</span>
                    <span className="ml-auto font-bold">{categoryLabels[property.category]}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Calendar className="h-5 w-5 text-gold-700" />
                    <span className="text-slate-600">發布日期</span>
                    <span className="ml-auto font-bold">{property.publishDate || '-'}</span>
                  </div>
                </div>

                {agent && (
                  <div className="border-t border-slate-100 pt-5">
                    <p className="mb-3 text-sm text-slate-500">負責人</p>
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-lg font-bold text-slate-600">
                          {agent.name.charAt(0)}
                        </div>
                        <div>
                          <p className="font-bold">{agent.name}</p>
                          <p className="text-xs text-slate-500">{agent.title}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-slate-400">歡迎查詢</p>
                        <p className="text-lg font-bold text-slate-800">
                          {agent.whatsapp.replace(/\D/g, '').replace(/^852/, '')}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex flex-col gap-3 border-t border-slate-100 pt-5">
                  <ShareButton
                    url={shareUrl}
                    title={property.title}
                    description={`${property.location} · ${property.address}`}
                    label="分享此物業"
                    className="flex min-h-[52px] items-center justify-center gap-2 rounded-full border border-slate-300 bg-white px-4 text-sm font-bold text-slate-700 transition-colors hover:bg-slate-50"
                  />
                  <a
                    href={whatsappUrl}
            data-wa-label={agent?.conversionLabel || ''}
                    target="_blank"
                    rel="noreferrer"
                    className="flex min-h-[52px] items-center justify-center gap-2 rounded-full bg-[#1f9d61] px-4 text-sm font-bold text-white transition-colors hover:bg-[#17814e]"
                  >
                    <MessageCircle className="h-5 w-5" fill="currentColor" />
                    WhatsApp 聯絡 {agent?.name || '我們'}
                  </a>
                  <button
                    type="button"
                    onClick={() => setShowMap(true)}
                    className="flex min-h-[52px] items-center justify-center gap-2 border border-slate-300 bg-white px-4 text-sm font-bold text-slate-700 transition-colors hover:bg-slate-50"
                  >
                    <Map className="h-5 w-5" />
                    查看地圖
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Details */}
        <section className="container mx-auto px-4 py-12">
          <div className="grid gap-10 lg:grid-cols-[1fr_360px]">
            <div className="space-y-10">
              <div>
                <h2 className="mb-4 text-xl font-bold text-slate-900">物業簡介</h2>
                <p className="leading-relaxed text-slate-700 whitespace-pre-line">{property.description}</p>
              </div>

              {property.features.length > 0 && (
                <div>
                  <h2 className="mb-4 text-xl font-bold text-slate-900">設施與特色</h2>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {property.features.map((feature) => (
                      <div key={feature} className="flex items-center gap-3 rounded-lg bg-[#f5f3ee] px-4 py-3">
                        <Check className="h-4 w-4 text-gold-700" />
                        <span className="text-sm font-semibold text-slate-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {property.tags.length > 0 && (
                <div>
                  <h2 className="mb-4 text-xl font-bold text-slate-900">標籤</h2>
                  <div className="flex flex-wrap gap-2">
                    {property.tags.map((tag) => (
                      <span key={tag} className="flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-sm font-semibold text-slate-600">
                        <Tag className="h-3 w-3" />
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {hasFloorPlan && (
                <div>
                  <h2 className="mb-4 text-xl font-bold text-slate-900">平面圖</h2>
                  <button
                    onClick={() => setShowFloorPlan(true)}
                    className="relative flex aspect-video w-full max-w-lg items-center justify-center overflow-hidden rounded-xl bg-slate-100 hover:bg-slate-200"
                  >
                    <img src={property.floorPlan} alt="平面圖" className="h-full w-full object-contain" />
                  </button>
                </div>
              )}
            </div>

            <div className="space-y-6">
              <div className="rounded-xl bg-slate-50 p-6">
                <h3 className="mb-4 flex items-center gap-2 font-bold text-slate-900">
                  <MapPin className="h-5 w-5 text-gold-700" />
                  地址
                </h3>
                <p className="mb-4 text-slate-700">{property.address}</p>
                <button
                  type="button"
                  onClick={() => setShowMap(true)}
                  className="text-sm font-bold text-blue-800 hover:underline"
                >
                  在 Google Maps 開啟
                </button>
              </div>

              <div className="rounded-xl bg-slate-50 p-6">
                <h3 className="mb-4 flex items-center gap-2 font-bold text-slate-900">
                  <Home className="h-5 w-5 text-gold-700" />
                  關於金晉物業
                </h3>
                <p className="text-sm leading-relaxed text-slate-600">
                  {cmsData.siteConfig.description}
                </p>
                <Link
                  to="/contact"
                  className="mt-4 inline-block text-sm font-bold text-blue-800 hover:underline"
                >
                  聯絡我們 →
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      <MapModal
        isOpen={showMap}
        onClose={() => setShowMap(false)}
        title={property.title}
        address={property.address}
        mapLink={property.mapLink}
      />

      {hasFloorPlan && (
        <div
          className={`fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 ${showFloorPlan ? '' : 'hidden'}`}
          onClick={() => setShowFloorPlan(false)}
        >
          <div className="relative max-h-[90vh] w-full max-w-4xl overflow-auto rounded-xl bg-white p-2 shadow-2xl">
            <img src={property.floorPlan} alt="平面圖" className="w-full rounded-lg" />
          </div>
        </div>
      )}

      {/* 圖片燈箱：完整顯示（object-contain），箭嘴/鍵盤/滑動導航，點背景關閉 */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-[90] flex flex-col bg-black/95"
          onClick={() => setLightboxOpen(false)}
          onTouchStart={(e) => setLightboxTouchStart(e.targetTouches[0].clientX)}
          onTouchEnd={(e) => {
            if (lightboxTouchStart === null) return;
            const diff = lightboxTouchStart - e.changedTouches[0].clientX;
            if (diff > 50) setActiveImage((prev) => (prev + 1) % images.length);
            else if (diff < -50) setActiveImage((prev) => (prev - 1 + images.length) % images.length);
            setLightboxTouchStart(null);
          }}
        >
          {/* 頂列：計數器 + 關閉 */}
          <div className="flex items-center justify-between p-4 text-white">
            <span className="rounded bg-white/15 px-3 py-1 text-sm font-bold backdrop-blur-sm">
              {activeImage + 1} / {images.length}
            </span>
            <button
              type="button"
              onClick={() => setLightboxOpen(false)}
              className="rounded-full bg-white/15 p-2.5 backdrop-blur-sm transition hover:bg-white/30"
              aria-label="關閉"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* 大圖 */}
          <div className="relative flex flex-1 items-center justify-center overflow-hidden px-2 pb-2">
            <img
              src={images[activeImage]}
              alt={`${property.title} ${activeImage + 1}`}
              className="max-h-full max-w-full rounded-lg object-contain shadow-2xl"
              draggable={false}
              onClick={(e) => e.stopPropagation()}
            />

            {images.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveImage((prev) => (prev - 1 + images.length) % images.length);
                  }}
                  className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-white/15 p-3 text-white backdrop-blur-sm transition hover:bg-white/30 md:p-4"
                  aria-label="上一張"
                >
                  <ChevronLeft className="h-6 w-6 md:h-8 md:w-8" />
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveImage((prev) => (prev + 1) % images.length);
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-white/15 p-3 text-white backdrop-blur-sm transition hover:bg-white/30 md:p-4"
                  aria-label="下一張"
                >
                  <ChevronRight className="h-6 w-6 md:h-8 md:w-8" />
                </button>
              </>
            )}
          </div>

          {/* 底部縮圖導航 */}
          {images.length > 1 && (
            <div
              className="flex justify-start gap-2 overflow-x-auto p-3"
              onClick={(e) => e.stopPropagation()}
            >
              {images.map((img, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setActiveImage(idx)}
                  className={`h-14 w-20 flex-shrink-0 overflow-hidden rounded border-2 transition ${
                    idx === activeImage ? 'border-amber-400' : 'border-transparent opacity-50 hover:opacity-100'
                  }`}
                >
                  <img src={img} alt="" className="h-full w-full object-cover" loading="lazy" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PropertyDetail;
